package com.zi.support.web

import com.zi.support.shared.CompanyDirectory
import com.zi.support.shared.EmptyState
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.Event
import kotlin.js.Date
import kotlin.js.jsTypeOf

internal fun element(id: String): HTMLElement? = document.getElementById(id) as? HTMLElement
internal fun input(id: String): String = (document.getElementById(id) as? HTMLInputElement)?.value?.trim().orEmpty()
internal fun textarea(id: String): String = (document.getElementById(id) as? HTMLTextAreaElement)?.value?.trim().orEmpty()
internal fun select(id: String): String = (document.getElementById(id) as? HTMLSelectElement)?.value.orEmpty()
internal fun radioValue(name: String): String = (document.querySelector("input[name=\"$name\"]:checked") as? HTMLInputElement)?.value.orEmpty()
internal fun checked(id: String): Boolean = (document.getElementById(id) as? HTMLInputElement)?.checked == true

/** Firebase's web SDK returns a plain JavaScript unsubscribe function. */
internal fun cancelListener(listener: dynamic) {
    if (listener == null || jsTypeOf(listener) != "function") return
    try { listener() } catch (_: Throwable) { }
}

internal fun click(id: String, action: (Event) -> Unit) {
    document.getElementById(id)?.addEventListener("click", action)
}

internal fun change(id: String, action: (Event) -> Unit) {
    document.getElementById(id)?.addEventListener("change", action)
}

internal fun type(id: String, action: (Event) -> Unit) {
    document.getElementById(id)?.addEventListener("input", action)
}

internal fun bindSearchClear(id: String, onClear: () -> Unit) {
    val input = document.getElementById(id) as? HTMLInputElement ?: return
    val shell = input.parentElement as? HTMLElement ?: return
    val button = document.createElement("button") as HTMLElement
    button.className = "search-clear"
    button.setAttribute("type", "button")
    button.setAttribute("aria-label", "Clear search")
    button.setAttribute("title", "Clear search")
    button.innerHTML = "<span class=\"material-symbols-rounded\" aria-hidden=\"true\">close</span>"
    shell.appendChild(button)
    fun sync() { button.style.display = if (input.value.isBlank()) "none" else "grid" }
    input.addEventListener("input", { sync() })
    button.addEventListener("click", {
        input.value = ""
        sync(); onClear(); input.focus()
    })
    sync()
}

internal fun escape(value: Any?): String = (value?.toString() ?: "")
    .replace("&", "&amp;").replace("<", "&lt;").replace(">", "&gt;")
    .replace("\"", "&quot;").replace("'", "&#39;")

/** "‹ More" link for screens opened from the More page; call [bindMoreBack] after rendering it. */
internal const val MORE_BACK = "<button id=\"back-to-more\" class=\"kb-back\"><span class=\"material-symbols-rounded\">arrow_back</span>More</button>"
internal fun bindMoreBack() = click("back-to-more") { WebApp.navigate("more") }

/** A Firestore query snapshot's documents as WebRecords. */
internal fun records(snapshot: dynamic): List<WebRecord> {
    val output = mutableListOf<WebRecord>(); val docs = snapshot.docs
    for (i in 0 until (docs.length as Int)) output += WebRecord(docs[i].id as String, docs[i].ref.path as String, docs[i].data())
    return output
}

internal fun field(data: dynamic, name: String): String = when (val value = if (data == null) null else data[name]) {
    null -> ""
    else -> value.toString()
}

internal fun stringList(value: dynamic): List<String> {
    if (value == null) return emptyList()
    val output = mutableListOf<String>()
    val length = value.length as? Int ?: return emptyList()
    for (index in 0 until length) output += value[index]?.toString().orEmpty()
    return output.filter { it.isNotBlank() }
}

internal fun timestampMillis(value: dynamic): Double {
    if (value == null) return 0.0
    return try { value.toDate().getTime() as Double } catch (_: Throwable) {
        Date.parse(value.toString())
    }
}

internal fun createdMillis(data: dynamic): Double {
    for (key in listOf("createdAt", "timestamp", "reportedAt", "completedAt", "updatedAt")) {
        val millis = timestampMillis(if (data == null) null else data[key])
        if (millis > 0) return millis
    }
    return 0.0
}

internal fun formatDate(value: dynamic): String {
    val millis = timestampMillis(value)
    if (millis <= 0) return "Pending"
    val date: dynamic = Date(millis)
    return date.toLocaleString("en-ZA", json("dateStyle" to "medium", "timeStyle" to "short")) as String
}

internal fun relativeTime(value: dynamic): String {
    val millis = timestampMillis(value)
    if (millis <= 0) return "just now"
    val seconds = kotlin.math.max(0, (((js("Date.now()") as Double) - millis) / 1000).toLong())
    return when {
        seconds < 60 -> "just now"
        seconds < 3600 -> "${seconds / 60} minute${if (seconds / 60 == 1L) "" else "s"} ago"
        seconds < 86400 -> "${seconds / 3600} hour${if (seconds / 3600 == 1L) "" else "s"} ago"
        seconds < 2592000 -> "${seconds / 86400} day${if (seconds / 86400 == 1L) "" else "s"} ago"
        seconds < 31536000 -> "${seconds / 2592000} month${if (seconds / 2592000 == 1L) "" else "s"} ago"
        else -> "${seconds / 31536000} year${if (seconds / 31536000 == 1L) "" else "s"} ago"
    }
}

/** Companies added in Settings, for the company pickers and [slug]. Loaded once per sign-in; failures keep the built-in list. */
internal fun loadSavedCompanies() {
    val fs = FirebaseGateway.firestore
    promise(FirebaseGateway.getCollection("companies", arrayOf(fs.where("companyName", ">", ""))), { snapshot ->
        val rows = records(snapshot)
        CompanyDirectory.load(rows.map { it.id }, rows.map { it.data.companyName as? String ?: "" })
    }, { })
}

internal fun slug(value: String): String {
    CompanyDirectory.savedId(value)?.let { return it }
    val clean = value.trim().lowercase().replace("&", " and ").replace(Regex("[^a-z0-9]+"), "-").trim('-')
    return when {
        "gantrans" in clean || clean == "gt" -> "gantrans"
        "kph" in clean -> "kph"
        "tyetrans" in clean || "tye-trans" in clean -> "tyetrans"
        "transport" in clean -> "transport"
        "delta" in clean -> "delta"
        clean == "fcl" -> "fcl"
        "avemel" in clean -> "avemel"
        "bees" in clean -> "bees-cartridge"
        "frenz" in clean -> "frenz-logistics"
        "rungatrans" in clean -> "rungatrans"
        clean == "sts" -> "sts"
        "texas" in clean -> "texas"
        "xmoor" in clean -> "xmoor"
        clean == "zi" -> "zi"
        else -> clean.ifBlank { "unassigned" }.take(80)
    }
}

/**
 * Counts every `[data-count]` element up from zero to its real value. Purely presentational -
 * the final number is exactly what was rendered, this only animates how it arrives, so a
 * dashboard reads like a scoreboard filling in rather than a table appearing.
 */
internal fun animateCounts(root: String) {
    val container = element(root) ?: return
    val nodes = container.querySelectorAll("[data-count]")
    for (index in 0 until nodes.length) {
        val node = nodes.item(index) as? HTMLElement ?: continue
        val target = node.getAttribute("data-count")?.toIntOrNull() ?: continue
        if (target <= 0) { node.textContent = "0"; continue }
        val steps = minOf(target, 22)
        var step = 0
        node.textContent = "0"
        val timer = window.setInterval({
            step++
            val progress = 1.0 - step.toDouble() / steps
            val eased = 1.0 - progress * progress * progress
            node.textContent = kotlin.math.round(target * eased).toInt().coerceAtMost(target).toString()
        }, 26)
        window.setTimeout({ window.clearInterval(timer); node.textContent = target.toString() }, 26 * steps + 40)
    }
}

internal fun toast(message: String, error: Boolean = false) {
    val root = element("toast-root") ?: return
    val node = document.createElement("div") as HTMLElement
    node.className = "toast${if (error) " error" else ""}"
    node.textContent = message
    root.appendChild(node)
    window.setTimeout({ node.remove() }, 4200)
}

/** Same name → same colour; sums character codes exactly like Android `avatarColor` and iOS `Color.avatar`. */
internal fun avatarColor(name: String): String {
    val palette = listOf("#4F7BF7", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#06B6D4", "#EF6C4D")
    return palette[name.lowercase().sumOf { it.code } % palette.size]
}

internal fun avatarHtml(name: String, label: String = name.trim().take(1).uppercase().ifBlank { "#" }): String =
    "<span class=\"chat-avatar tone-avatar\" style=\"background:${avatarColor(name)}\" aria-hidden=\"true\">${escape(label)}</span>"

/**
 * "Ticket created" confirmation (mirrors Android/iOS): the ticket slides up as if printed, a check stamps
 * onto it, confetti bursts, then it fades away after a moment or on click. Animation lives in styles.css.
 */
internal fun ticketCreatedCelebration(ticketNumber: String, detail: String, completed: Boolean = false, onClosed: () -> Unit = {}) {
    document.getElementById("ticket-created")?.remove()
    val node = document.createElement("div") as HTMLElement
    node.id = "ticket-created"
    node.className = if (completed) "tc-overlay tc-completed" else "tc-overlay"
    node.setAttribute("role", "status")
    node.setAttribute("aria-live", "polite")
    val dots = (0 until 8).joinToString("") { "<i style=\"--a:${it * 45 + 20}deg;--d:${38 + (it % 3) * 10}px\"></i>" }
    node.innerHTML = """
        <div class="tc-card">
          <div class="tc-stage">
            <div class="tc-ticket"><span class="material-symbols-rounded" aria-hidden="true">confirmation_number</span><strong>${escape(ticketNumber)}</strong></div>
            <div class="tc-check"><div class="tc-burst" aria-hidden="true">$dots</div><span class="material-symbols-rounded" aria-hidden="true">check</span></div>
          </div>
          <h3>${if (completed) "Ticket completed" else "Ticket created"}</h3><p>${escape(detail)}</p>
        </div>
    """.trimIndent()
    document.body?.appendChild(node)
    var closed = false
    fun close() {
        if (closed) return
        closed = true
        node.classList.add("leaving")
        window.setTimeout({ node.remove(); onClosed() }, 240)
    }
    node.addEventListener("click", { close() })
    window.setTimeout({ close() }, 2600)
}

/**
 * Opens a popup, replacing any open ones. [layered] opens it on top instead (e.g. Assign over the ticket
 * details), so closing it returns to the popup underneath.
 */
internal fun modal(title: String, body: String, actions: String = "", wide: Boolean = false, extraClass: String = "", layered: Boolean = false) {
    if (!layered) closeAllModals()
    val node = document.createElement("div") as HTMLElement
    node.className = "modal-backdrop"
    node.setAttribute("data-modal", "")
    node.innerHTML = """
        <section class="modal${if (wide) " wide" else ""}${if (extraClass.isBlank()) "" else " $extraClass"}" role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <header class="modal-head"><h2 id="modal-title">${escape(title)}</h2><button class="btn btn-icon btn-ghost modal-close" aria-label="Close">✕</button></header>
          <div class="modal-body">$body</div>
          ${if (actions.isBlank()) "" else "<footer class=\"modal-actions\">$actions</footer>"}
        </section>
    """.trimIndent()
    document.body?.appendChild(node)
    val close = node.querySelector(".modal-close") as HTMLElement
    close.addEventListener("click", { node.remove() })
    // Popups close only from the ✕ (or Escape), never from a click outside, so a stray click can't lose work.
    node.addEventListener("keydown", { event -> if (event.asDynamic().key == "Escape") node.remove() })
    close.focus()
}

/**
 * A yes/no popup (log out, delete, resolve…): a tinted [icon] over the title, a short [message], then Cancel and
 * [confirmLabel] side by side. Cancel closes it; the caller binds [confirmId]. [tone] is danger, success or primary.
 */
internal fun confirmModal(title: String, message: String, confirmId: String, confirmLabel: String, icon: String = "delete",
                          tone: String = "danger", cancelLabel: String = "Cancel", layered: Boolean = false) {
    modal(title, "<p class=\"confirm-message\">${escape(message)}</p>",
        "<button id=\"confirm-cancel\" class=\"btn\" type=\"button\">${escape(cancelLabel)}</button><button id=\"$confirmId\" class=\"btn btn-$tone\" type=\"button\">${escape(confirmLabel)}</button>",
        extraClass = "confirm-modal", layered = layered)
    val heads = document.querySelectorAll(".confirm-modal .modal-head")
    (heads.item(heads.length - 1) as? HTMLElement)?.insertAdjacentHTML("afterbegin",
        "<div class=\"confirm-icon confirm-$tone\" aria-hidden=\"true\"><span class=\"material-symbols-rounded\">$icon</span></div>")
    click("confirm-cancel") { closeModal() }
}

/** Closes the top popup, showing the one underneath (if any). */
/**
 * Shows one of the shared pages (map, route, call) in [frame]. A blob URL, not srcdoc: an srcdoc page's location is
 * about:srcdoc, so MapLibre's workers (which only accept messages from the page's own origin) drop every tile and the map
 * stays empty. A blob page keeps this site's origin.
 */
internal fun showPage(frame: dynamic, html: String) {
    val options: dynamic = js("({})")
    options.type = "text/html"
    val blob = js("Reflect").construct(js("Blob"), arrayOf(arrayOf(html), options))
    frame.src = js("URL").createObjectURL(blob) as String
}

internal fun closeModal() { document.querySelectorAll("[data-modal]").let { nodes -> if (nodes.length > 0) (nodes.item(nodes.length - 1) as HTMLElement).remove() } }

internal fun closeAllModals() { document.querySelectorAll("[data-modal]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).remove() } }

/**
 * Picker popup: a searchable list of (value, label, optional count) rows with the current choice ticked.
 * [allLabel] adds a first "everything" row with value "". With [confirmLabel], tapping only selects and the
 * footer button commits; otherwise a tap picks and closes. [leading] replaces a row's avatar (HTML for its value).
 */
internal fun pickOption(title: String, icon: String, allLabel: String?, options: List<Triple<String, String, Int?>>, selected: String,
                        note: String = "", confirmLabel: String? = null, layered: Boolean = false, searchable: Boolean = options.size > 6, leading: ((String) -> String)? = null, onPick: (String) -> Unit) {
    var choice = selected
    fun row(value: String, label: String, count: Int?) = "<button type=\"button\" class=\"filter-option${if (value == selected) " selected" else ""}\" data-filter-value=\"${escape(value)}\" data-filter-label=\"${escape(label.lowercase())}\" aria-pressed=\"${value == selected}\">" +
        (if (value.isEmpty()) "<span class=\"filter-option-icon\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">$icon</span></span>" else leading?.invoke(value) ?: avatarHtml(label)) +
        "<span class=\"filter-option-label\">${escape(label)}</span>${count?.let { "<span class=\"filter-option-count${if (it == 0) " zero" else ""}\">$it</span>" }.orEmpty()}" +
        "<span class=\"material-symbols-rounded filter-option-check\" aria-hidden=\"true\">check</span></button>"
    val search = if (searchable) "<div class=\"search-shell filter-search\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">search</span><label class=\"visually-hidden\" for=\"filter-search\">Search</label><input id=\"filter-search\" class=\"input search\" type=\"search\" placeholder=\"Search…\" autocomplete=\"off\"></div>" else ""
    modal(title, "$search<div class=\"filter-options\" role=\"list\">${allLabel?.let { row("", it, null) }.orEmpty()}${options.joinToString("") { row(it.first, it.second, it.third) }}</div>" +
        "<p id=\"filter-none\" class=\"filter-none\" hidden>No matches.</p>${if (note.isBlank()) "" else "<p class=\"filter-note\">${escape(note)}</p>"}",
        confirmLabel?.let { "<button id=\"filter-confirm\" class=\"btn btn-primary\">${escape(it)}</button>" }.orEmpty(), extraClass = "filter-modal", layered = layered)
    (document.getElementById("filter-search") as? HTMLInputElement)?.let { box ->
        box.focus()
        box.addEventListener("input", {
            val q = box.value.trim().lowercase(); var shown = 0
            document.querySelectorAll("[data-filter-label]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).let { n ->
                val hit = q.isEmpty() || n.getAttribute("data-filter-label")!!.contains(q); n.hidden = !hit; if (hit) shown++
            } }
            document.getElementById("filter-none")?.let { (it as HTMLElement).hidden = shown > 0 }
        })
    }
    document.querySelectorAll("[data-filter-value]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).let { node ->
        node.addEventListener("click", {
            choice = node.getAttribute("data-filter-value").orEmpty()
            if (confirmLabel == null) { onPick(choice); closeModal(); return@addEventListener }
            for (j in 0 until nodes.length) (nodes.item(j) as HTMLElement).let { other ->
                val on = other === node; other.classList.toggle("selected", on); other.setAttribute("aria-pressed", "$on")
            }
        })
    } }
    document.getElementById("filter-confirm")?.addEventListener("click", { onPick(choice) })
}

/**
 * Fades out and removes the first-paint boot splash (see index.html), revealing
 * whatever real screen has just been rendered into #app underneath it. Safe to
 * call repeatedly - a no-op once the splash element is gone.
 */
internal fun revealApp() {
    val splash = document.getElementById("boot-splash") as? HTMLElement ?: return
    splash.classList.add("leaving")
    window.setTimeout({ splash.remove() }, 340)
}

internal fun setBusy(busy: Boolean) {
    val top = element("page-loading") ?: return
    top.style.display = if (busy) "block" else "none"
}

/** Cards matching [selector] lean towards the pointer (CSS reads --rx/--ry/--mx/--my). Skipped for reduced motion. */
internal fun enableTilt(selector: String) {
    if (kotlinx.browser.window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    val cards = document.querySelectorAll(selector)
    for (i in 0 until cards.length) {
        val card = cards.item(i) as? org.w3c.dom.HTMLElement ?: continue
        card.addEventListener("pointermove", { event ->
            val rect = card.getBoundingClientRect()
            val x = ((event.asDynamic().clientX as Double) - rect.left) / rect.width
            val y = ((event.asDynamic().clientY as Double) - rect.top) / rect.height
            card.style.setProperty("--ry", "${(x - 0.5) * 14}deg"); card.style.setProperty("--rx", "${(0.5 - y) * 12}deg")
            card.style.setProperty("--mx", "${x * 100}%"); card.style.setProperty("--my", "${y * 100}%")
        })
        card.addEventListener("pointerleave", { card.style.setProperty("--rx", "0deg"); card.style.setProperty("--ry", "0deg") })
    }
}

/** A ticket update still uploading its photo/video/file: shown under the updates until the real update arrives. */
internal fun uploadingUpdateHtml(fileName: String, percent: Int): String =
    """<div class="card update-card update-uploading" role="status"><span class="spinner-mini" aria-hidden="true"></span><div><strong>Uploading ${escape(fileName)}… $percent%</strong><small> It will appear here when it finishes.</small><div class="upload-bar"><i style="width:$percent%"></i></div></div></div>"""

/** Every empty list looks the same: the icon in a soft blue circle, a bold title, a muted line, an optional button. Same layout as Android and iOS. */
internal fun emptyState(state: EmptyState, icon: String, actionId: String = "", actionLabel: String = ""): String =
    "<div class=\"empty-state\" role=\"status\"><span class=\"empty-state-icon material-symbols-rounded\" aria-hidden=\"true\">$icon</span><strong>${escape(state.title)}</strong><p>${escape(state.body)}</p>" +
        (if (actionId.isBlank()) "" else "<button id=\"$actionId\" class=\"btn\">${escape(actionLabel)}</button>") + "</div>"

/**
 * A figure at the top of Tickets, Issues, Checklists and Site visits: a tinted icon, the number and its label.
 * [tone] is brand, amber, blue, green, red or slate. With [of] (the total it's part of), a bar shows its share.
 */
internal fun statCard(label: String, value: Any, icon: String, tone: String = "brand", of: Int? = null): String {
    val share = if (of != null && of > 0 && value is Int) value * 100 / of else null
    return "<article class=\"stat-card stat-$tone\"><div class=\"stat-icon\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">$icon</span></div>" +
        "<div class=\"stat-body\"><div class=\"stat-value\">$value</div><div class=\"stat-label\">${escape(label)}</div>" +
        (if (share == null) "" else "<div class=\"stat-share\" title=\"$share% of all\"><i style=\"width:$share%\"></i></div><small class=\"stat-note\">$share% of all</small>") +
        "</div></article>"
}

/**
 * Every native <select> on the site shows as a button that opens the [pickOption] popup (search on long lists, the
 * current choice ticked). The select stays in the page, hidden, so screens keep reading .value and listening for
 * "change"; setting .value from code updates the button too. A <select multiple> or data-native keeps the browser's.
 */
internal fun installSelectPickers() {
    // Code sets select.value directly (driver filters, forms): tell the button.
    js("""(function () {
        var p = HTMLSelectElement.prototype;
        ['value', 'selectedIndex'].forEach(function (name) {
            var d = Object.getOwnPropertyDescriptor(p, name);
            Object.defineProperty(p, name, {configurable: true, enumerable: d.enumerable, get: function () { return d.get.call(this); },
                set: function (v) { d.set.call(this, v); this.dispatchEvent(new Event('pickerrefresh')); }});
        });
    })()""")
    fun titleOf(select: HTMLSelectElement): String {
        val forLabel = select.id.takeIf { it.isNotBlank() }?.let { document.querySelector("label[for='${it}']") }
        val text = (forLabel ?: select.closest("label"))?.textContent?.replace("*", "")?.trim().orEmpty()
        return text.ifBlank { select.getAttribute("aria-label").orEmpty() }.ifBlank { "Choose an option" }
    }
    fun refresh(select: HTMLSelectElement, trigger: HTMLElement) {
        val chosen = select.asDynamic().selectedOptions[0]
        trigger.querySelector(".select-trigger-label")?.textContent = (chosen?.textContent as? String)?.trim().orEmpty().ifBlank { "Choose…" }
        trigger.asDynamic().disabled = select.disabled
        trigger.classList.toggle("placeholder", select.value.isBlank())
    }
    fun enhance(select: HTMLSelectElement) {
        if (select.multiple || select.hasAttribute("data-picker") || select.hasAttribute("data-native") || select.closest(".gjs-editor") != null) return
        select.setAttribute("data-picker", "")
        val trigger = document.createElement("button") as HTMLElement
        trigger.setAttribute("type", "button")
        trigger.className = "input select-trigger"
        trigger.setAttribute("aria-haspopup", "dialog")
        trigger.setAttribute("aria-label", titleOf(select))
        trigger.innerHTML = "<span class=\"select-trigger-label\"></span><span class=\"material-symbols-rounded select-trigger-caret\" aria-hidden=\"true\">expand_more</span>"
        select.classList.add("select-native"); select.tabIndex = -1; select.setAttribute("aria-hidden", "true")
        select.parentNode?.insertBefore(trigger, select.nextSibling)
        refresh(select, trigger)
        val people = Regex("assign|user|owner|driver|attendee|technician", RegexOption.IGNORE_CASE).containsMatchIn(select.id)
        trigger.addEventListener("click", {
            val options = (0 until select.options.length).mapNotNull { i ->
                val option = select.options.item(i).asDynamic()
                if (option.disabled == true) null else Triple(option.value as String, (option.textContent as String).trim(), null as Int?)
            }
            pickOption(titleOf(select), "remove", null, options, select.value, layered = select.closest("[data-modal]") != null,
                leading = if (people) null else { _ -> "" }) { value ->
                select.value = value
                select.dispatchEvent(Event("input", js("({bubbles: true})")))
                select.dispatchEvent(Event("change", js("({bubbles: true})")))
            }
        })
        select.addEventListener("pickerrefresh", { refresh(select, trigger) })
        select.addEventListener("change", { refresh(select, trigger) })
        val watch: (dynamic, dynamic) -> Unit = { _, _ -> refresh(select, trigger) }
        js("new MutationObserver(watch)").observe(select, js("({childList: true, subtree: true, attributes: true, characterData: true})"))
    }
    fun scan(root: dynamic) {
        if (root.tagName == "SELECT") enhance(root.unsafeCast<HTMLSelectElement>())
        if (root.querySelectorAll == undefined) return
        val nodes = root.querySelectorAll("select")
        for (i in 0 until (nodes.length as Int)) enhance(nodes[i].unsafeCast<HTMLSelectElement>())
    }
    scan(document.body)
    val added: (dynamic, dynamic) -> Unit = { records, _ ->
        for (r in 0 until (records.length as Int)) { val nodes = records[r].addedNodes; for (n in 0 until (nodes.length as Int)) if (nodes[n].nodeType == 1) scan(nodes[n]) }
    }
    js("new MutationObserver(added)").observe(document.body, js("({childList: true, subtree: true})"))
}
